import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { TokenStats } from "../api/types";
import { explorerAddress } from "../env";
import { formatRcd, formatSol } from "../overlay/format";

/** /token — transparency page from /v1/token/stats, refreshed every minute (§22.5). */
export function Token() {
  const [stats, setStats] = useState<TokenStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const load = () =>
      api
        .tokenStats()
        .then((s) => live && (setStats(s), setError(null)))
        .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    void load();
    const id = setInterval(load, 60_000);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, []);

  return (
    <main className="page token">
      <h1>RCD transparency</h1>
      <p>
        RCD supply is fixed at the premint. Playing moves in-game balances up and down against the game reserve; it never creates or destroys tokens. The mint
        authority and liquidity position are held by multisigs and changes are announced at least 14 days ahead.
      </p>
      {error && <p className="status error">{error}</p>}
      {!stats && !error && <p>Loading…</p>}
      {stats && (
        <>
          <dl className="kv wide">
            <dt>Total supply</dt>
            <dd>{formatRcd(stats.supply)}</dd>
            <dt>Game reserve</dt>
            <dd>{formatRcd(stats.reserve)}</dd>
            <dt>Held in games (all players)</dt>
            <dd>{formatRcd(stats.totalGameBalance)}</dd>
            <dt>Fees accrued (not yet swept)</dt>
            <dd>{formatRcd(stats.feesAccrued)}</dd>
            <dt>Treasury</dt>
            <dd>{formatRcd(stats.treasury)}</dd>
            <dt>Free reserve</dt>
            <dd>{formatRcd((BigInt(stats.reserve) - BigInt(stats.totalGameBalance) - BigInt(stats.feesAccrued)).toString())}</dd>
            {stats.pool.rcd !== null && stats.pool.sol !== null && (
              <>
                <dt>Pool</dt>
                <dd>
                  {formatRcd(stats.pool.rcd)} / {formatSol(stats.pool.sol)} SOL
                  {stats.pool.price !== null && <> · {stats.pool.price.toFixed(0)} lamports per ₽1</>}
                </dd>
              </>
            )}
            <dt>Mint authority</dt>
            <dd>
              {stats.mintAuthority ? (
                <a href={explorerAddress(stats.mintAuthority)} target="_blank" rel="noreferrer">
                  {stats.mintAuthority}
                </a>
              ) : (
                "revoked"
              )}
            </dd>
          </dl>
          <h2>Rare items</h2>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Tokens in wallets</th>
                <th>Held in games</th>
              </tr>
            </thead>
            <tbody>
              {stats.items.map((i) => (
                <tr key={i.index}>
                  <td>{i.name}</td>
                  <td>{i.index === 0 ? (i.masterBallsMinted ?? i.supply) : i.supply}</td>
                  <td>{i.inGameTotal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
